<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
import ProgressBar from '@/application/shared/infrastructure/components/ProgressBar.vue';
import AppNavigation from '@/application/shared/infrastructure/components/AppNavigation.vue';
import { LoadingView, PrimaryButton, ToastTypes } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { ApplicationSection } from '@/shared/application/ApplicationSection';
import { useSectionNavigation } from '@/shared/infrastructure/composition/SectionNavigation';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';
import SectionLetsBeginPreview from '@/application/smallBusiness/infrastructure/components/sections/SectionLetsBeginPreview.vue';
import SectionEmployees from '@/application/smallBusiness/infrastructure/components/sections/SectionEmployees.vue';
import SectionCompanyInfo from '@/application/smallBusiness/infrastructure/components/sections/SectionCompanyInfo.vue';
import SectionSelectPlan from '@/application/smallBusiness/infrastructure/components/sections/SectionSelectPlan.vue';
import SectionAppointment from '@/application/smallBusiness/infrastructure/components/sections/SectionAppointment.vue';
import SectionThankYou from '@/application/smallBusiness/infrastructure/components/sections/SectionThankYou.vue';
import LetUsHelpSection from '@/application/shared/infrastructure/components/LetUsHelpSection.vue';
import NavigationModals from '@/application/smallBusiness/infrastructure/components/NavigationModals.vue';
import SmallBusinessPlanFilters from '@/application/smallBusiness/infrastructure/components/SmallBusinessPlanFilters.vue';
import SmallBusinessSelectedPlanWrapper from '@/application/smallBusiness/infrastructure/components/SmallBusinessSelectedPlanWrapper.vue';
import SmallBusinessFaq from '@/application/smallBusiness/infrastructure/components/SmallBusinessFAQ.vue';
import SectionSelectPlanBanner from '@/application/smallBusiness/infrastructure/components/SectionSelectPlanBanner.vue';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import SectionContentInsurance from '@/application/smallBusiness/infrastructure/components/sections/SectionContentInsurance.vue';
import SelectPlanMock from '@/application/smallBusiness/infrastructure/components/SelectPlanMock.vue';
import { APPLICATION_SETTING_TYPES } from '@/shared/domain/GlobalSetting';

const { appStore, appState } = useAppStore();
const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();

const { getRouteNameByAppType, getSmallBusinessConsultationPhone } = useUrlHelper();

interface SmallBusinessSection extends ApplicationSection {
  hideLetUsHelp?: boolean;
  hideNavigation?: boolean;
  showSelectedPlan?: boolean;
  showPlanFilters?: boolean;
  showFAQ?: boolean;
  showSelectPlanBanner?: boolean;
  ignoreSubmitValidation?: boolean;
  completeOnStepValid?: boolean,
}

// we imply section order from this array
const sectionList: Array<SmallBusinessSection> = [{
  component: SectionLetsBeginPreview,
  shortTitle: 'Let\'s Begin',
  hideLetUsHelp: true,
  hideNavigation: true
}, {
  component: SectionEmployees,
  shortTitle: 'Employees',
  conditionalShow: (): boolean => smallBusinessState.value.application.detailed_employees,
}, {
  component: SectionSelectPlan,
  shortTitle: 'Select Plan',
  showPlanFilters: true,
  showFAQ: true,
  showSelectPlanBanner: true
}, {
  component: SectionCompanyInfo,
  shortTitle: 'Company Info',
  showSelectedPlan: true,
  completeOnStepValid: true,
}, {
  component: SectionAppointment,
  shortTitle: 'Schedule',
  conditionalShow: (): boolean => !smallBusinessState.value.isSmallCompany && !smallBusinessState.value.hasAppointment,
  hideLetUsHelp: true,
  ignoreSubmitValidation: true,
  nextHidden: () => true
}, {
  component: SectionThankYou,
  hideLetUsHelp: true,
  shortTitle: 'Thank You'
}];

const stepRef = ref<InstanceType<typeof SectionCompanyInfo> | null>(null);
const loading = ref(false);
const progressBar = ref(false);

const reachedThanksKey = getRouteNameByAppType(APPLICATION_TYPES.SMALL_BUSINESS, 'ReachedThanks');

const markFlowAsCompleted = (): void => {
  localStorage.setItem(reachedThanksKey, 'true');
};

const {
  showProgressBar,
  filteredSectionList,
  currentSection,
  nextSection,
  prevSection,
  goToSection,
  resetSection
} = useSectionNavigation<SmallBusinessSection>(sectionList, stepRef, false, true, markFlowAsCompleted);

const showStickyPlanBox = computed(() => filteredSectionList.value[currentSection.value].showSelectedPlan === true && (!smallBusinessState.value.availablePlansLoading && smallBusinessState.value.application.plans?.length > 0));
const hideNavigation = ref(false);

const saveApplicationAndContinue = async (callBack: () => void): Promise<void> => {
  loading.value = true;

  if (!filteredSectionList.value[currentSection.value].ignoreSubmitValidation) {
    const response = await smallBusinessStore.saveApplicationOnLocalStorage();
    if (response && response.error) {
      throwErrorAndRestart(response.error.message);

      return;
    }
  }
  loading.value = false;
  callBack();
};

const checkSaveAndExitInfo = async (email: string): Promise<void> => {
  if (email !== smallBusinessState.value.application.company.primary_contact?.email) {
    await smallBusinessStore.updateCompany({ primary_contact: { ...smallBusinessState.value.application.company.primary_contact, email: email } });
  }

  if (smallBusinessState.value.application.applicants.length === 0 && smallBusinessState.value.companySize > 0) {
    await smallBusinessStore.addEmployee(smallBusinessState.value.companySize);
  }

  if (filteredSectionList.value[currentSection.value].completeOnStepValid) {
    await stepRef.value?.sectionSubmit();
  }
  await smallBusinessStore.saveApplicationOnLocalStorage();
};

const disableRemovePlans = (): boolean => {
  return true;
};

onBeforeMount(async () => {
  if (localStorage.getItem(reachedThanksKey)) {
    localStorage.removeItem(reachedThanksKey);
    smallBusinessStore.clearAndRestart();

    return;
  }

  await smallBusinessStore.initAppFromLocalStorage();
});

onMounted(async () => {
  await appStore.getGlobalSettings();
  await appStore.updateCustomTitleSlot('content');
  await appStore.updateCustomTitle('Find Affordable Business Health Plans');
  await appStore.setShowFooter(false);
  smallBusinessStore.clearReviewData();
});

onBeforeUnmount(async () => {
  await appStore.updateCustomTitleSlot('title');
  await appStore.updateCustomTitle('');
  await appStore.updateCustomTitleClass('');
  await appStore.setShowFooter(true);
});

function clearAndRestart (): void {
  smallBusinessStore.clearAndRestart();
  resetSection();
}

const isQuotingDisabled = computed<boolean>(() => appState.value.globalSettings?.applications[APPLICATION_SETTING_TYPES.SMALL_BUSINESS]?.quotingEnabled === false);

const throwErrorAndRestart = (message: string): void => {
  appStore.addToast({ message: message, type: ToastTypes.DANGER });
  clearAndRestart();
};
</script>

<template>
  <div v-if="appState.loadingGlobalSettings">
    <LoadingView />
  </div>
  <template v-else>
    <div v-if="isQuotingDisabled" class="flex w-full justify-center mt-6">
      <div class="flex flex-col items-center w-full max-w-2xl bg-white border border-gray-300 rounded-lg px-6 py-12 text-center">
        <div class="flex items-center justify-center w-16 h-16 rounded-full bg-orange-100">
          <hfc-icon icon="triangle-exclamation" class="text-orange-400 text-2xl" />
        </div>
        <h2 class="mt-6 max-w-sm">Sorry our public quoting system is down!</h2>
        <p class="mt-2 text-lg">Please call <a class="font-semibold" tabindex="-1" :href="`tel:${getSmallBusinessConsultationPhone()}`">{{ getSmallBusinessConsultationPhone() }}.</a> for a quote.</p>
        <PrimaryButton class="mt-6 px-16" size="xl" :href="`tel:${getSmallBusinessConsultationPhone()}`">
          <hfc-icon icon="phone" class="mr-2" />Call {{ getSmallBusinessConsultationPhone() }}
        </PrimaryButton>
      </div>
    </div>
    <template v-else>
      <SectionSelectPlanBanner v-if="filteredSectionList[currentSection].showSelectPlanBanner" @go-to-section="val => goToSection(val, true)" />
      <div class="relative" :key="smallBusinessState.application.uuid">
      <template v-if="currentSection === 0">
        <SelectPlanMock class="absolute inset-0 -mx-3 sm:-mx-6 overflow-hidden" />
        <div class="absolute inset-0 -mx-3 sm:-mx-6 bg-black/30"></div>
      </template>
      <div class="relative flex w-full lg:justify-center flex-col lg:flex-row mt-6 min-h-[calc(100vh-112px)]">
        <div class="hidden xl:flex flex-col xl:w-64 xl:pr-4 xl:shrink-0">
          <SmallBusinessPlanFilters v-if="filteredSectionList[currentSection].showPlanFilters" />
        </div>
        <div class="flex xl:max-w-4xl 2xl:max-w-7xl  flex-col w-full mb-3 xl:min-w-0">
          <div class="flex w-full flex-col">
            <div class="flex items-start flex-col flex-grow mb-3">
              <div v-if="progressBar" class="py-3 w-full">
                <ProgressBar :current-section="currentSection" v-if="showProgressBar" :section-list="filteredSectionList" @goToSection="goToSection"></ProgressBar>
              </div>
              <div class="flex-grow flex flex-col w-full">
                <div v-if="!smallBusinessState.loadingApplication && !appState.loadingGlobalSettings" class="w-full flex flex-col">
                  <component :is="filteredSectionList[currentSection].component" ref="stepRef" v-bind="filteredSectionList[currentSection].props" @prevSection="prevSection" @nextSection="saveApplicationAndContinue(nextSection)" @setLoading="(v: boolean) => loading = v" @goToSection="goToSection" @hideNavigation="hideNavigation = true">
                  </component>
                </div>
                <div class="mt-2" v-if="!filteredSectionList[currentSection].hideLetUsHelp">
                  <LetUsHelpSection />
                </div>
                <AppNavigation v-if="!hideNavigation && !filteredSectionList[currentSection].hideNavigation" :current-step="currentSection" :total-steps="filteredSectionList.length - 1" @prevSection="prevSection" @nextSection="saveApplicationAndContinue(nextSection)" @submit="saveApplicationAndContinue(nextSection)" :loading="loading || appState.loadingCoverage" :next-hidden="filteredSectionList[currentSection].nextHidden?.() ? [currentSection] : undefined" submit-text="Continue">
                  <template #after-continue>
                    <NavigationModals :show-save-and-exit="true" :show-clear-and-restart="true" :application-type="APPLICATION_TYPES.SMALL_BUSINESS" :uuid="smallBusinessState.application.uuid" :email="smallBusinessState.application.company.primary_contact?.email" :application="smallBusinessState.application" @resetSection="resetSection()" :save-and-exit-callback="checkSaveAndExitInfo" />
                  </template>
                  <template #after-submit>
                    <NavigationModals :show-save-and-exit="true" :show-clear-and-restart="true" :application-type="APPLICATION_TYPES.SMALL_BUSINESS" :uuid="smallBusinessState.application.uuid" :email="smallBusinessState.application.company.primary_contact?.email" :application="smallBusinessState.application" @resetSection="resetSection()" :save-and-exit-callback="checkSaveAndExitInfo" />
                  </template>
                </AppNavigation>
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col xl:w-64 xl:pl-4 xl:shrink-0">
          <SmallBusinessSelectedPlanWrapper v-if="showStickyPlanBox" :disable-remove-plans="disableRemovePlans()" @clear-and-restart="clearAndRestart()" />
          <SmallBusinessFaq v-if="filteredSectionList[currentSection].showFAQ === true" />
        </div>
      </div>
      </div>
      <div v-if="currentSection === 0" class="flex w-full">
        <SectionContentInsurance />
      </div>
      </template>
  </template>
</template>
